import * as React from "react";
import { Button, Dropdown, notification, Tooltip } from "antd";
import { ChevronDown } from "react-feather";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../utils/time";
import { ReleaseUninstallModal } from "../../pages/marketplace/release/deleteModal";
import { MarketplaceReleaseInstance } from "../../pages/marketplace/types/release";
import { useAppSelector } from "../../store/hooks";
import { dateString } from "../../components/dateStringFn";
import { DateString } from "../../components/dateString";
import { selectLabel } from "../../store/timezoneSlice";
import { clearItem, yamlDumpAll, toSortedJSON } from "../../services/utils";
import { convertAllLinksToRelative } from "../../utils/linkParser/linkParser";
import { linksOf, request } from "../../services/cpln";

interface Props {
  release: MarketplaceReleaseInstance;
  items?: any[];
}
export const TemplateReleaseDetailHeader: React.FC<Props> = ({ release, items = [] }) => {
  const timezoneLabel = useAppSelector(selectLabel);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [action, setAction] = React.useState<"uninstall" | "">("");

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  async function handleExport(format: "json" | "yaml", slim: boolean) {
    try {
      // Reveal secrets before export, fallback to unrevealed if fails
      const revealedItems = await Promise.all(
        items.map(async (item) => {
          if (item.kind === "secret") {
            try {
              const { data } = await request({ url: linksOf(item).reveal! });
              return data;
            } catch {
              return item;
            }
          }
          return item;
        }),
      );

      let exportItems = revealedItems.map((item) => {
        let _item = JSON.parse(JSON.stringify(item));
        if (slim) {
          clearItem(_item);
        }
        _item = convertAllLinksToRelative(_item);
        _item = toSortedJSON(_item);
        return _item;
      });

      const fileName = `marketplace-${release.template}-${release.name}`;

      if (format === "json") {
        const blob = JSON.stringify(exportItems, null, 2);
        const file = new Blob([blob], { type: "text/json" });
        const href = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.style.display = "none";
        a.download = `${fileName}.json`;
        a.href = href;
        a.click();
      } else {
        const blob = yamlDumpAll(exportItems, { indent: 2, noRefs: true });
        const file = new Blob([blob], { type: "text/yaml" });
        const href = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.style.display = "none";
        a.download = `${fileName}.yaml`;
        a.href = href;
        a.click();
      }

      notification.success({ message: "Export initiated" });
    } catch (e) {
      notification.warning({ message: "Export failed", description: e.message });
    }
  }

  function handleActionsClick({ key }: any) {
    switch (key) {
      case "export-json":
        handleExport("json", false);
        break;
      case "export-yaml":
        handleExport("yaml", false);
        break;
      case "export-json-slim":
        handleExport("json", true);
        break;
      case "export-yaml-slim":
        handleExport("yaml", true);
        break;
      case "uninstall":
        setAction("uninstall");
        break;
      default:
        break;
    }
  }

  const menuItems: ItemType<MenuItemType>[] = [];
  if (items.length > 0) {
    const exportItem = {
      key: "ignore2",
      label: "Export",
      children: [
        { key: "export-json", label: "JSON" },
        { key: "export-yaml", label: "YAML" },
        { key: "export-json-slim", label: "JSON Slim" },
        { key: "export-yaml-slim", label: "YAML Slim" },
      ],
    };
    menuItems.push(exportItem);
  }
  menuItems.push({ key: "uninstall", label: "Uninstall", danger: true });

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            Template Release
          </div>
          <div className="flex items-center">
            <div data-testid="detail-name" className="text-2xl font-semibold leading-none">
              {release.name}
            </div>
          </div>
        </div>
        <div className="flex-grow" />

        <div className="flex items-center gap-2">
          <div>{`Revision ${release.revision}`}</div>
          {menuItems.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: menuItems, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(release.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(release.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(release.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(release.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(release.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(release.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={release.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
          </>
        </div>
      </div>
      {action === "uninstall" ? (
        <ReleaseUninstallModal release={release} onClose={() => setAction("")} onDone={() => setAction("")} />
      ) : null}
    </>
  );
};
